import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import type { CategoryPickerBodyProps } from './categoryPickerTypes';

// ENG-2551: the folded form of a category picker on phones. A sticky,
// horizontally scrollable chip row so the thumbnail grid owns the viewport
// while switching categories stays one tap. --chrome-offset is set by the
// .order-container / .margin-for-incentive-tracker* classes (definitions.scss)
// so the row pins just below whatever fixed chrome is present.
const ChipsRow = styled.div`
  position: sticky;
  top: var(--chrome-offset, 52px);
  z-index: 10;
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  gap: 8px;
  padding: 8px 0 12px;
  // The page ground, not white: at rest the row must not read as a box on the
  // grey page (see --page-ground in definitions.scss).
  background: var(--page-ground, #f8f8f8);
  scrollbar-width: none;
  // Fade the trailing edge so it reads as "more categories this way" —
  // mobile scroll containers show no scrollbar to hint it themselves.
  -webkit-mask-image: linear-gradient(to right, black 88%, transparent 100%);
  mask-image: linear-gradient(to right, black 88%, transparent 100%);
  // ENG-2551 P1 fix: this row can't wrap, so its min-content width is the sum
  // of every chip (~1000px for 8 categories). On the phone breakpoint,
  // ConfigScreenWrapper sets align-items: center, so ItemPanel (its flex
  // child) is sized shrink-to-fit instead of stretched to the viewport. A
  // shrink-to-fit ancestor recurses into an auto/percent-width descendant's
  // content to size itself, so that ~1000px min-content propagated straight
  // up through CategoryFoldWrapper (width: 100%, itself a pass-through) and
  // forced the whole panel, including the sibling thumbnail grid, out to
  // ~1000px, off both edges of the viewport.
  //
  // width: 0 gives this box a definite specified width, so ancestors no
  // longer recurse into its content when computing their own shrink-to-fit
  // size (they just see 0). min-width: 100% then overrides that 0 during
  // the real layout pass, once the ancestor chain has already resolved to the
  // viewport-bound width, capping this row at its parent's width and letting
  // overflow-x: auto scroll the rest instead of stretching the parent.
  box-sizing: border-box;
  width: 0;
  min-width: 100%;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const chipFace = `
  flex: 0 0 auto;
  padding: 8px 14px;
  border-radius: 16px;
  font-size: 13px;
  font-weight: 800;
  white-space: nowrap;

  &:focus-visible {
    outline: 2px solid #479de3;
    outline-offset: 2px;
  }
`;

const Chip = styled.button<{ selected?: boolean }>`
  ${chipFace}
  color: ${(props) => (props.selected ? 'white' : '#479DE3')};
  border: ${(props) => (props.selected ? 'none' : '1px solid #479DE3')};
  background: ${(props) => (props.selected ? '#479DE3' : 'white')};
`;

// The way back to the full category list. Pinned to the left edge of the
// scroller: it used to sit in the normal scroll flow, and the selected chip
// auto-centring below would push it off-screen, leaving no reachable route
// back once a customer picked a category several chips along.
const ExpandChip = styled.button`
  ${chipFace}
  position: sticky;
  left: 0;
  z-index: 1;
  color: #479de3;
  border: 1px solid #479de3;
  // Opaque so scrolling chips pass underneath rather than showing through.
  background: white;
  // Paints an opaque gutter on both sides: the left block covers the row's
  // padding so a chip can't peek out at the viewport edge, and the right block
  // keeps the chip scrolling underneath from touching this one's border (two
  // overlapping pills read as a rendering bug). The soft shadow sits outside
  // that gutter so the row clearly passes *under* this control.
  box-shadow: -6px 0 0 0 var(--page-ground, #f8f8f8),
    10px 0 0 0 var(--page-ground, #f8f8f8), 14px 0 8px -6px rgba(0, 0, 0, 0.18);
`;

const CategoryChips = ({
  categories = [],
  selectedIndex,
  onSelect,
  onExpand,
}: CategoryPickerBodyProps) => {
  const selectedRef = useRef<HTMLButtonElement>(null);

  // Bring the selected chip into view when the row appears — the customer may
  // have tapped the 8th category in the expanded list.
  useEffect(() => {
    if (selectedRef.current && selectedRef.current.scrollIntoView) {
      selectedRef.current.scrollIntoView({
        inline: 'center',
        block: 'nearest',
      });
    }
  }, [selectedIndex]);

  // One background set is not a choice — showing a picker with a single chip is
  // noise. Nothing to collapse, so render nothing.
  if (categories.length <= 1) return null;

  return (
    <ChipsRow role="group" aria-label="Background categories">
      {/* An action, not a toggle, so it carries no aria-pressed state. */}
      <ExpandChip type="button" onClick={onExpand}>
        ☰ All
      </ExpandChip>
      {categories.map((category, index) => {
        const selected = index === selectedIndex;
        return (
          <Chip
            type="button"
            // Falls back to the index because category names are
            // studio-authored and not guaranteed unique.
            key={category.id ?? index}
            selected={selected}
            aria-pressed={selected}
            ref={selected ? selectedRef : null}
            onClick={() => onSelect(category, index)}
          >
            {category.name}
          </Chip>
        );
      })}
    </ChipsRow>
  );
};

export default CategoryChips;
